import { useEffect, useRef } from 'react'
import { Lock } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import type { WindowNavigationState } from '@krp/client-runtime'
import { ActionInfo, SelectField, Tabs, type TabItem } from '@krp/ui-kit'

import { MenuIcon } from './MenuIcon'
import { SECTION_TABS_PREFIX, type SectionTab } from './sectionTabs'

/**
 * Полоса вкладок раздела (UI-SECTIONS) на `Tabs` ui-kit: tablist, стрелки и
 * Home/End, у выбранной — черта и жирность, а не только цвет. Вкладка — адрес
 * окна: выбор переходит по нему, поэтому открытое видно в адресе, F5 и «назад»
 * браузера работают без своего состояния (CLAUDE.md §7). Метка вида ресурса
 * («1С», «AQSI») — значок перед именем; у собственной вкладки входа — значок
 * раздела.
 */
export function SectionTabStrip({
  label,
  icon,
  tabs,
  activeId,
  returnTo,
}: {
  label: string
  icon: string | null
  tabs: readonly SectionTab[]
  activeId: string
  /** «Откуда пришли» в раздел: переход между вкладками его наследует — «← Назад» уводит из раздела, а не по вкладкам. */
  returnTo: string | undefined
}) {
  const navigate = useNavigate()
  const strip = useRef<HTMLDivElement>(null)
  // Узкий экран: полоса прокручивается вбок, и открытая вкладка (адрес из закладки, F5) не должна остаться за краем.
  useEffect(() => {
    const selected = strip.current?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')
    if (selected != null && typeof selected.scrollIntoView === 'function') selected.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [activeId])
  const items: TabItem[] = tabs.map((tab) => ({
    id: tab.id,
    label: tab.subtitle === null ? tab.title : `${tab.title} · ${tab.subtitle}`,
    icon:
      tab.badge === null ? (
        <MenuIcon name={icon} size={15} />
      ) : (
        <>
          {/* Пробел после метки — для диктора: «1С 1С Основная», а не «1С1С Основная»; в конце строки он не виден. */}
          <span className="shell-section-kind" {...(tab.tone === null ? {} : { 'data-source': tab.tone })}>{`${tab.badge} `}</span>
          {/* Замок, а не только бледность: недоступная вкладка читается и в оттенках серого (CLAUDE.md §7). */}
          {tab.locked && <Lock size={14} aria-hidden="true" />}
        </>
      ),
    ...(tab.locked ? { disabled: true, hint: `В источнике ${tab.badge ?? tab.kind} нет раздела «${label}»` } : {}),
  }))

  const select = (id: string) => {
    const tab = tabs.find((item) => item.id === id)
    if (tab === undefined || id === activeId) return
    const state: WindowNavigationState = { windowFrom: returnTo ?? null }
    void navigate(tab.path, { state })
  }

  // Телефон: вкладок бывает восемь, и полоса из них в 2500 px прятала открытую за краем, а имена резала посреди
  // слова (аудит 15.10, п. 10). Там вместо полосы — список выбора с полными именами; заблокированных в нём нет.
  const options = tabs
    .filter((tab) => !tab.locked || tab.id === activeId)
    .map((tab) => ({
      value: tab.id,
      label: [tab.badge, tab.subtitle === null ? tab.title : `${tab.title} · ${tab.subtitle}`].filter((part) => part !== null).join(' '),
    }))

  return (
    <div className="shell-section-tabs" ref={strip}>
      <ActionInfo id="shell.section-tab" label={`Вкладки раздела «${label}»`} layout="block">
        <Tabs label={`Вкладки раздела «${label}»`} items={items} selected={activeId} onSelect={select} idPrefix={SECTION_TABS_PREFIX} />
      </ActionInfo>
      <div className="shell-section-select">
        <SelectField label={`Источник раздела «${label}»`} value={activeId} options={options} onChange={select} actionId="shell.section-tab" />
      </div>
    </div>
  )
}
